import type { Room } from "building-types/rooms/roomTypes";
import type { SpaceType } from "building-types/rooms/spaceTypesEnum";
import type { ElectricalRule, RangeFilter, RoomTypeSpaceTypeFilter } from "building-types/settings/electricalRuleTypes";
import { aabb2XLength, aabb2YLength } from "geom/aabb2";
import { polyLine2AABB2, polyLine2Area } from "geom/polyLine2";
import { ElectricalLayoutTypesAbbrevations } from "./electricalLayoutUtils";

export function electricalLayoutRuleTextSummary(rule: ElectricalRule): string {
  const { filters, electricalLayout } = rule;

  const negateToString = (s: string, n: boolean): string => {
    return n ? ` !${s}` : s;
  };

  const rangeToString = (f: RangeFilter): string => {
    const { minimum, maximum } = f;
    if (minimum !== undefined && maximum !== undefined) {
      return `${minimum} - ${maximum}`;
    } else if (minimum !== undefined) {
      return `> ${minimum}`;
    } else if (maximum !== undefined) {
      return `< ${maximum}`;
    }
    return "";
  };

  let summaryString = "" + (ElectricalLayoutTypesAbbrevations[electricalLayout.layoutType] ?? "-") + ":";

  if (filters.roomType && filters.roomType.length > 0) {
    summaryString += ` rm [`;
    for (const index of Object.keys(filters.roomType)) {
      const filter = filters.roomType[index];
      summaryString += negateToString(filter.roomType as string, !!filter.negate);
      if (filters.roomType[parseInt(index) + 1]) {
        summaryString += ", ";
      }
    }
    summaryString += `]`;
  }

  if (filters.spaceType && filters.spaceType.length > 0) {
    summaryString += ` spc [`;
    for (const index of Object.keys(filters.spaceType)) {
      const filter = filters.spaceType[index];
      summaryString += negateToString(filter.spaceType as string, !!filter.negate);
      if (filters.spaceType[parseInt(index) + 1]) {
        summaryString += ", ";
      }
    }
    summaryString += `]`;
  }

  if (filters.area && filters.area.length > 0) {
    summaryString += ` ar [`;
    for (const index of Object.keys(filters.area)) {
      const filter = filters.area[index];
      summaryString += negateToString(rangeToString(filter), !!filter.negate);
      if (filters.area[parseInt(index) + 1]) {
        summaryString += ", ";
      }
    }
    summaryString += `]`;
  }

  if (filters.area && filters.area.length > 0) {
    summaryString += ` wd [`;
    for (const index in filters.width) {
      const filter = filters.width[index];
      summaryString += negateToString(rangeToString(filter), !!filter.negate);
      if (filters.width[parseInt(index) + 1]) {
        summaryString += ", ";
      }
    }
    summaryString += `]`;
  }

  if (filters.length && filters.length.length > 0) {
    summaryString += ` ln [`;
    for (const index of Object.keys(filters.length)) {
      const filter = filters.length[index];
      summaryString += negateToString(rangeToString(filter), !!filter.negate);
      if (filters.length[parseInt(index) + 1]) {
        summaryString += ", ";
      }
    }
    summaryString += `]`;
  }

  return summaryString;
}

export function shouldApplyElectricalLayoutRulesToRoom(
  room: Room,
  roomSpaceType: SpaceType,
  filters: RoomTypeSpaceTypeFilter,
): boolean {
  if (!room || !room.outline || !roomSpaceType) {
    return false;
  }

  const { outline } = room;

  // todo: aligned bounding box for diagonal rooms
  const bb = polyLine2AABB2(outline);
  const xLength = aabb2XLength(bb);
  const yLength = aabb2YLength(bb);

  const rType = room.type;
  const roomArea = polyLine2Area(outline) / 144;
  const roomWidth = Math.min(xLength, yLength);
  const roomLength = Math.max(xLength, yLength);

  const { roomType, spaceType, area, width, length } = filters;

  let noFilters = true;
  let roomOrSpaceTypeFilter = false;
  let matchesAtLeastOneRoomOrSpaceTypeFilter = false;

  if (roomType && roomType.length > 0) {
    roomOrSpaceTypeFilter = true;
    noFilters = false;
    for (const { roomType: r, negate } of roomType) {
      if (negate && r === rType) {
        return false;
      } else if (r === rType) {
        matchesAtLeastOneRoomOrSpaceTypeFilter = true;
      }
    }
  }

  if (spaceType && spaceType.length > 0) {
    roomOrSpaceTypeFilter = true;
    noFilters = false;
    for (const { spaceType: s, negate } of spaceType) {
      if (negate && s === roomSpaceType) {
        return false;
      } else if (s === roomSpaceType) {
        matchesAtLeastOneRoomOrSpaceTypeFilter = true;
      }
    }
  }

  if (area && area.length > 0) {
    let matchesAtLeastOne = false;
    noFilters = false;
    for (const { minimum, maximum, negate } of area) {
      if (negate && numberFallsWithinRange(minimum, maximum, roomArea)) {
        return false;
      } else if (numberFallsWithinRange(minimum, maximum, roomArea)) {
        matchesAtLeastOne = true;
      }
    }
    if (!matchesAtLeastOne) {
      return false;
    }
  }

  if (width && width.length > 0) {
    let matchesAtLeastOne = false;
    noFilters = false;
    for (const { minimum, maximum, negate } of width) {
      if (negate && numberFallsWithinRange(minimum, maximum, roomWidth)) {
        return false;
      } else if (numberFallsWithinRange(minimum, maximum, roomWidth)) {
        matchesAtLeastOne = true;
      }
    }
    if (!matchesAtLeastOne) {
      return false;
    }
  }

  if (length && length.length > 0) {
    let matchesAtLeastOne = false;
    noFilters = false;
    for (const { minimum, maximum, negate } of length) {
      if (negate && numberFallsWithinRange(minimum, maximum, roomLength)) {
        return false;
      } else if (numberFallsWithinRange(minimum, maximum, roomLength)) {
        matchesAtLeastOne = true;
      }
    }
    if (!matchesAtLeastOne) {
      return false;
    }
  }

  if (noFilters) {
    return true;
  }

  if (roomOrSpaceTypeFilter && !matchesAtLeastOneRoomOrSpaceTypeFilter) {
    return false;
  }

  return true;
}

function numberFallsWithinRange(minimum: number | undefined, maximum: number | undefined, n: number): boolean {
  let passes = true;

  if (minimum !== undefined && maximum !== undefined) {
    const rMin = Math.min(minimum, maximum);
    const rMax = Math.max(minimum, maximum);
    if (n < rMin) {
      passes = false;
    } else if (n >= rMax) {
      passes = false;
    }
  } else if (minimum !== undefined) {
    if (n < minimum) {
      passes = false;
    }
  } else if (maximum !== undefined) {
    if (n >= maximum) {
      passes = false;
    }
  }

  return passes;
}
